/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';

import { IHelp, IOrderSummary, IPaymentMethods } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/../../common';
import {
    IGroup,
    IGroupDelivery,
    IGroups
} from '@msdyn365-commerce-modules/order-management/src/modules/order-details/../../common/get-groups';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/../../common/get-order-information';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/../../common/get-sales-line';
import { IOrderDetailsViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/./order-details';
// @ts-ignore
import { BookPublished } from '@churchofjesuschrist/eden-icons';
import { getStoreHoursAsync, getRetailChannelAsync, getWarehouseAsync } from '../../../actions/DataActionExtension.g';
// @ts-ignore
import { TruckFilled, OfficeLarge } from '@churchofjesuschrist/eden-icons';

interface IPaymentExtended extends IPaymentMethods {
    userId?: any;
    wardId?: any;
}
interface IGroupsExtended extends IGroups {
    itemCount?: any;
    nonSubItems?: any;
    resources?: any;
    subItems?: any;
    bopis?: any;
}
interface IGroupExtended extends IGroup {
    itemCount?: any;
    nonSubItems?: any;
    subItems?: any;
    resources?: any;
    bopis?: any;
    itemNum: any;
}
interface IGroupDeliveryExtended extends IGroupDelivery {
    itemCount?: any;
    nonSubItems?: any;
    subItems?: any;
    resources?: any;
    salesLines?: any;
}

export const OrderDetailsOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {channelAddress}
        {channelReferenceId}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
    </Node>
);

export const OrderDetailsSalesLine: React.FC<ISalesLine> = props => {
    const { salesLineProps, salesLine, buyAgainButton, errors } = props;
    return (
        <Node {...salesLineProps}>
            {salesLine}
            {buyAgainButton}
            {errors}
        </Node>
    );
};

export const OrderDetailsGroupDelivery: React.FC<IGroupDeliveryExtended> = ({ deliveryProps, heading, count, resources, salesLines }) => {
    return (
        <Node {...deliveryProps}>
            {salesLines[0].data.product?.ExtensionProperties[0].Value?.BooleanValue === true && (
                <div
                    className='ms-order-details__group-delivery-heading'
                    style={{ display: 'flex', alignItems: 'center', fontSize: '18px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ marginRight: '5px', display: 'flex' }}>
                            <BookPublished size='29px' />
                        </div>
                        <div style={{ marginRight: '5px' }}>{resources.subscriptionLabel}</div>
                        <div>{count}</div>
                    </div>
                </div>
            )}
            {salesLines[0].data.product?.ExtensionProperties[0].Value?.BooleanValue === false && heading}
            {salesLines[0].data.product?.ExtensionProperties[0].Value?.BooleanValue === false && count}
        </Node>
    );
};

export const OrderDetailsGroup: React.FC<IGroupExtended> = ({
    groupProps,
    delivery,
    address,
    salesLinesProps,
    salesLines,
    isCashAndCarryTransaction,
    itemCount,
    nonSubItems,
    resources,
    bopis
}) => {
    return (
        <Node {...groupProps}>
            {delivery && (
                <OrderDetailsGroupDelivery
                    {...delivery}
                    itemCount={itemCount}
                    nonSubItems={nonSubItems}
                    resources={resources}
                    salesLines={salesLines}
                />
            )}
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map(salesLine => {
                        //@ts-ignore
                        if (address && salesLine.data.product.ExtensionProperties[0].Value.BooleanValue === true) {
                            //@ts-ignore
                            address.props.resource.shipToLabel = resources.deliverToLabel;
                        }

                        return (
                            <React.Fragment key={salesLine.data.salesLine.LineId}>
                                {salesLine.data.deliveryType === 'ship' && salesLine.data.shipment ? salesLine.trackingInfo : null}
                                <OrderDetailsSalesLine {...salesLine} />
                                {/* @ts-ignore */}
                                {salesLine.data.product.ExtensionProperties[0].Value.BooleanValue === true && (
                                    <div className='ms-order-details__sales-status'>{resources.deliveryLabel}</div>
                                )}

                                {!isCashAndCarryTransaction && nonSubItems.length > 0 && salesLine.salesStatus}
                            </React.Fragment>
                        );
                    })}
                </Node>
            )}
            {!delivery.showTimeslot && address}
            {delivery.showTimeslot && (
                <Node {...delivery.pickupProps}>
                    {address}
                    {bopis && bopis.PHONE && <div>{bopis.PHONE}</div>}
                    {delivery.pickupDateTimeslot}
                </Node>
            )}
        </Node>
    );
};

export const OrderDetailsGroups: React.FC<IGroupsExtended> = ({
    groupsProps,
    groups,
    itemCount,
    nonSubItems,
    subItems,
    resources,
    bopis
}) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => {
            return (
                <OrderDetailsGroup
                    key={index}
                    {...group}
                    itemCount={itemCount}
                    nonSubItems={nonSubItems}
                    subItems={subItems}
                    resources={resources}
                    itemNum={index}
                    bopis={bopis}
                />
            );
        })}
    </Node>
);

export const OrderDetailsOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderDetailsPayment: React.FC<IPaymentExtended> = ({ paymentMethodsProps, title, methods, wardId, userId }) => {
    let accountNum;
    if (
        methods &&
        methods[0] &&
        //@ts-ignore
        methods[0].props &&
        //@ts-ignore
        methods[0].props.children &&
        //@ts-ignore
        methods[0].props.children[0] &&
        //@ts-ignore
        methods[0].props.children[0].props &&
        //@ts-ignore
        methods[0].props.children[0].props.children &&
        //@ts-ignore
        methods[0].props.children[0].props.children[2]
    ) {
        //@ts-ignore
        accountNum = methods[0].props.children[0].props.children[2];
    }
    let isVisible = false;
    if (accountNum !== userId && wardId) {
        isVisible = true;
    }
    return (
        <Node {...paymentMethodsProps}>
            {title}
            {isVisible ? (
                <>
                    <div className='ms-order-details__payment-methods-customer-account'>{wardId}</div>
                    {/* @ts-ignore */}
                    <div>{methods && methods[0] && methods[0].props && methods[0].props.children[1]}</div>
                </>
            ) : null}
            {methods}
        </Node>
    );
};

export const OrderDetailsPaymentHeading: React.FC<IPaymentExtended> = ({ paymentMethodsProps, title, methods, wardId, userId }) => {
    let accountNum;
    if (
        methods &&
        methods[0] &&
        //@ts-ignore
        methods[0].props &&
        //@ts-ignore
        methods[0].props.children &&
        //@ts-ignore
        methods[0].props.children[0] &&
        //@ts-ignore
        methods[0].props.children[0].props &&
        //@ts-ignore
        methods[0].props.children[0].props.children &&
        //@ts-ignore
        methods[0].props.children[0].props.children[2]
    ) {
        //@ts-ignore
        accountNum = methods[0].props.children[0].props.children[2];
    }
    let isVisible = false;
    if (accountNum !== userId && wardId) {
        isVisible = true;
    }

    return (
        <div className='lds-order-details-payment-heading'>
            <div className='lds-order-details-payment-heading-label'>
                {/* @ts-ignore */}
                {title && title.props && title.props.children}:
            </div>
            <div className='lds-order-details-payment-heading-value'>
                {/* @ts-ignore */}
                {methods && methods[0] && methods[0].props && methods[0].props.children[0].props.children}
            </div>
            {isVisible ? (
                <>
                    <div className='ms-order-details__payment-methods-customer-account lds-order-details-payment-heading-customer-account'>
                        {wardId}
                    </div>
                    <div className='lds-order-details-payment-heading-methods'>
                        {/* @ts-ignore */}
                        {methods && methods[0] && methods[0].props && methods[0].props.children[1]}
                    </div>
                </>
            ) : null}
            {/* {methods} */}
        </div>
    );
};

export const OrderDetailsHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

export const OrderDetailsHeading: React.FC<any> = props => {
    if (props && props.groups && props.groups.groups && props.groups.groups[0]) {
        props.groups.groups[0].address.props.resource.shipToLabel = '';
    }

    return (
        <div className='lds-order-details-heading-container'>
            <div className='lds-order-details-heading-title'>
                {props.resources.orderIdLabel} {props.salesOrder.SalesId}{' '}
                {/* {props &&
                    props.orderInfomation &&
                    props.orderInfomation.channelReferenceId &&
                    props.orderInfomation.channelReferenceId.props &&
                    props.orderInfomation.channelReferenceId.props.children &&
                    props.orderInfomation.channelReferenceId.props.children[0] &&
                    props.orderInfomation.channelReferenceId.props.children[0].props &&
                    props.orderInfomation.channelReferenceId.props.children[0].props.children &&
                    props.orderInfomation.channelReferenceId.props.children[0].props.children[0]}{' '} */}
                {`(${props &&
                    props.orderInfomation &&
                    props.orderInfomation.channelReferenceId &&
                    props.orderInfomation.channelReferenceId.props &&
                    props.orderInfomation.channelReferenceId.props.children &&
                    props.orderInfomation.channelReferenceId.props.children[1] &&
                    props.orderInfomation.channelReferenceId.props.children[1].props &&
                    props.orderInfomation.channelReferenceId.props.children[1].props.children &&
                    props.orderInfomation.channelReferenceId.props.children[1].props.children[0].props &&
                    props.orderInfomation.channelReferenceId.props.children[1].props.children[0].props.value})`}{' '}
            </div>
            <div
                className='lds-order-details-heading-content'
                style={{
                    justifyContent: props && props.orderSummary && props.orderSummary.shipping === '' ? 'flex-start' : 'space-around'
                }}
            >
                <div className='lds-order-details-heading-left'>
                    <div className='lds-order-details-heading-row'>
                        <div className='lds-order-details-heading-label'>{props.resources.orderDateTitle}:</div>
                        <div className='lds-order-details-heading-value'>
                            {props &&
                                props.orderInfomation &&
                                props.orderInfomation.createdDate &&
                                props.orderInfomation.createdDate.props &&
                                props.orderInfomation.createdDate.props.children}
                        </div>
                    </div>
                    <div className='lds-order-details-heading-row'>
                        <OrderDetailsPaymentHeading
                            {...props.payment}
                            userId={props.context.request.user.customerAccountNumber}
                            //@ts-ignore
                            wardId={props.context.request.query.wardId}
                        />
                    </div>
                    <div className='lds-order-details-heading-row'>
                        <div className='lds-order-details-heading-label'>{props.resources.orderStatusTitle}:</div>
                        <div className='lds-order-details-heading-value'>
                            {props.salesLines && props.salesLines.length > 0
                                ? props.salesLines[0].data.salesStatus.charAt(0).toUpperCase() +
                                  props.salesLines[0].data.salesStatus.slice(1)
                                : 'N/A'}
                        </div>
                    </div>
                </div>
                {/* {props && props.orderSummary && props.orderSummary.shipping != '' ? (
                    <div className='lds-order-details-heading-right'>
                        <div className='lds-order-details-heading-row'>
                            <div className='lds-order-details-heading-label'>{props.resources.addressTitle}: </div>
                            <div className='lds-order-details-heading-value'>
                                {props.groups && props.groups.groups && props.groups.groups.length > 0
                                    ? props.groups.groups[0].address
                                    : null}
                            </div>
                        </div>
                    </div>
                ) : null} */}
            </div>
        </div>
    );
};
export const OrderDetailsBody: React.FC<any> = props => {
    return (
        <div className='lds-order-details-body'>
            <div className='lds-order-details-body-container'>
                <div className='lds-order-details-body-content'>
                    <div className='lds-order-details-body-left' style={{ fontSize: '20px', fontWeight: 700 }}>
                        <div className='lds-order-details-body-icon'>
                            {props && props.orderSummary && props.orderSummary.shipping === '' ? (
                                <OfficeLarge size='29px' />
                            ) : (
                                <TruckFilled size='29px' />
                            )}
                        </div>
                        {props.salesOrder &&
                        props.salesOrder.DeliveryMode &&
                        String(props.salesOrder.DeliveryMode).toLowerCase() === 'pickup'
                            ? props.resources.pickUpText
                            : props.resources.headingImages}
                        {/* {props.groups &&
                            props.groups.groups &&
                            props.groups.groups[0] &&
                            props.groups.groups[0].delivery &&
                            props.groups.groups[0].delivery.heading &&
                            props.groups.groups[0].delivery.heading.props &&
                            props.groups.groups[0].delivery.heading.props.children} */}
                    </div>
                    <div className='lds-order-details-body-right'>
                        {props.orderInfomation &&
                            props.orderInfomation.count &&
                            props.orderInfomation.count.props &&
                            props.orderInfomation.count.props.children}
                    </div>
                </div>
            </div>
        </div>
    );
};
export const OrderShippingDetails: React.FC<any> = props => {
    // let shippingCost;
    // if (props.orderSummary && props.orderSummary.shipping && props.orderSummary.shipping.props && props.orderSummary.shipping.props.value) {
    //     shippingCost = props.context.cultureFormatter.formatCurrency(
    //         props.orderSummary.shipping.props.value,
    //         props.orderSummary.shipping.props.priceCurrency
    //     );
    // }
    return (
        <div className='lds-order-shipping-details'>
            <div className='lds-order-shipping-details-left'>
                <div className='lds-order-shipping-details-row'>
                    <div className='lds-order-shipping-details-label'>{props.resources.shippingAddressHeader}: </div>
                    <div className='lds-order-shipping-details-value'>
                        {props.groups && props.groups.groups && props.groups.groups.length > 0 ? props.groups.groups[0].address : null}
                    </div>
                </div>
            </div>
            <div className='lds-order-shipping-details-right'>
                {/* <div className='lds-order-shipping-details-delivery'>
                    {props.groups &&
                        props.groups.groups &&
                        props.groups.groups[0] &&
                        props.groups.groups[0].delivery &&
                        props.groups.groups[0].delivery.heading &&
                        props.groups.groups[0].delivery.heading.props &&
                        props.groups.groups[0].delivery.heading.props.children}
                </div> */}
                {/* <div className='lds-order-shipping-details-cost'>
                    <div>{props.resources.shippingText}</div>
                    <div>{shippingCost}</div>
                </div> */}
            </div>
        </div>
    );
};
export const OrderPickupDetails: React.FC<any> = props => {
    // Create a deep copy of the props to avoid modifying the original
    const orderInfo = props.orderInfomation ? { ...props.orderInfomation } : null;

    if (
        orderInfo &&
        orderInfo.channelReferenceId &&
        orderInfo.channelReferenceId.props &&
        orderInfo.channelReferenceId.props.children &&
        orderInfo.channelReferenceId.props.children[1] &&
        orderInfo.channelReferenceId.props.children[1].props &&
        orderInfo.channelReferenceId.props.children[1].props.children &&
        orderInfo.channelReferenceId.props.children[1].props.children[0] &&
        orderInfo.channelReferenceId.props.children[1].props.children[0].props
    ) {
        // Create a new object with the updated size property
        const updatedProps = {
            ...orderInfo.channelReferenceId.props.children[1].props.children[0].props,
            size: 150
        };

        // Create new objects for each level to avoid modifying the original
        const updatedChild = {
            ...orderInfo.channelReferenceId.props.children[1].props.children[0],
            props: updatedProps
        };

        const updatedChildren = [...orderInfo.channelReferenceId.props.children[1].props.children];
        updatedChildren[0] = updatedChild;

        const updatedProps1 = {
            ...orderInfo.channelReferenceId.props.children[1].props,
            children: updatedChildren
        };

        const updatedChild1 = {
            ...orderInfo.channelReferenceId.props.children[1],
            props: updatedProps1
        };

        const updatedChildren1 = [...orderInfo.channelReferenceId.props.children];
        updatedChildren1[1] = updatedChild1;

        const updatedProps2 = {
            ...orderInfo.channelReferenceId.props,
            children: updatedChildren1
        };

        const updatedChannelRef = {
            ...orderInfo.channelReferenceId,
            props: updatedProps2
        };

        orderInfo.channelReferenceId = updatedChannelRef;
    }

    return (
        <div className='lds-order-shipping-details'>
            <div className='lds-order-shipping-details-left'>
                <div className='lds-order-shipping-details-row'>
                    <div className='lds-order-pickup-details-code-container'>
                        <div>
                            {orderInfo &&
                                orderInfo.channelReferenceId &&
                                orderInfo.channelReferenceId.props &&
                                orderInfo.channelReferenceId.props.children &&
                                orderInfo.channelReferenceId.props.children[1] &&
                                orderInfo.channelReferenceId.props.children[1].props &&
                                orderInfo.channelReferenceId.props.children[1].props.children &&
                                orderInfo.channelReferenceId.props.children[1].props.children[0]}
                        </div>
                        <div className='lds-order-pickup-details-code-text'>{props.resources.qrCodeText}</div>
                    </div>
                </div>
            </div>
            <div className='lds-order-shipping-details-right lds-order-pickup-details-right'>
                <div className='lds-order-pickup-details-row'>
                    <div className='lds-order-pickup-details-label'>{props.resources.pickupInStoreText}</div>
                    <div className='lds-order-pickup-details-value'>
                        {props.groups && props.groups.groups && props.groups.groups.length > 0 ? props.groups.groups[0].address : null}
                    </div>
                </div>
                <div className='lds-order-pickup-details-row'>
                    <div className='lds-order-pickup-details-label'>
                        {props.groups &&
                            props.groups.groups &&
                            props.groups.groups[0] &&
                            props.groups.groups[0].delivery &&
                            props.groups.groups[0].delivery.pickupDateTimeslot &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children[0] &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children[0].props &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children[0].props.children}
                    </div>
                    <div className='lds-order-pickup-details-value'>
                        {props.groups.groups &&
                            props.groups.groups[0] &&
                            props.groups.groups[0].delivery &&
                            props.groups.groups[0].delivery.pickupDateTimeslot &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children[1] &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children[1].props &&
                            props.groups.groups[0].delivery.pickupDateTimeslot.props.children[1].props.children}
                    </div>
                </div>
            </div>
        </div>
    );
};
export const OrderItemDetails: React.FC<any> = props => {
    return (
        <div className='lds-order-item-details'>
            {props &&
                props.groups &&
                props.groups.groups &&
                props.groups.groups[0] &&
                props.groups.groups[0].salesLines &&
                props.groups.groups[0].salesLines.map((salesLine: any) => {
                    const fallbackImageUrl = getFallbackImageUrl(
                        salesLine.data.product?.ItemId || salesLine.data.salesLine?.ItemId || '',
                        props.context.actionContext.requestContext.apiSettings,
                        props.context.request.app?.config?.OmniChannelMedia
                    );
                    const productCost = props.context.cultureFormatter.formatCurrency(
                        salesLine.data.product?.Price || salesLine.data.salesLine?.Price || '',
                        props.orderInfomation.amount.props.currencyCode || props.orderSummary.shipping.props.priceCurrency
                    );
                    const totalCost = props.context.cultureFormatter.formatCurrency(
                        salesLine.data.salesLine?.TotalAmount || '',
                        props.orderInfomation.amount.props.currencyCode || props.orderSummary.shipping.props.priceCurrency
                    );
                    let productDescriptionName: string | undefined;
                    const salesLineDescription = salesLine.data.salesLine?.Description;
                    if (salesLineDescription) {
                        const descriptionParts = salesLineDescription.split('/');
                        if (descriptionParts.length > 1) {
                            productDescriptionName = descriptionParts[1];
                        }
                    }
                    let productName = salesLine.data.product?.Name || productDescriptionName || salesLine.data.salesLine?.Description || '';
                    return (
                        <div className='lds-order-details-item'>
                            <div className='lds-order-details-item-left'>
                                <img className='lds-order-details-item-image' src={fallbackImageUrl} alt='' />

                                <div className='lds-order-details-item-details'>
                                    <a
                                        href={salesLine.data.productUrl}
                                        style={{
                                            color: '#000',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {productName}
                                    </a>
                                    <span>{productCost}</span>
                                    {salesLine.data.product?.Dimensions?.map((dimension: any) => {
                                        return <span>{dimension.DimensionValue.Value}</span>;
                                    })}
                                    <a style={{ cursor: 'pointer' }} href={salesLine.data.productUrl}>
                                        {props.resources.buyItAgainLabel}
                                    </a>
                                </div>
                            </div>
                            <div className='lds-order-details-item-right'>
                                {salesLine.salesStatus.props.label}
                                <div className='lds-order-details-item-quantity'>
                                    <p style={{ marginLeft: '10px' }}>{salesLine.data.salesLine?.Quantity}</p>
                                </div>

                                <div className='lds-order-details-item-price'>
                                    <p>{totalCost}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};
export const OrderSummaryDetails: React.FC<any> = props => {
    let subtotalAmount = 0;
    let shippingAmount = 0;
    let taxAmount = 0;
    let totalAmount = 0;

    if (props.salesOrder) {
        subtotalAmount = props.salesOrder.SubtotalAmount || 0;
        shippingAmount = props.salesOrder.ShippingChargeAmount || 0;
        taxAmount = props.salesOrder.TaxAmount || 0;
        totalAmount = props.salesOrder.TotalAmount || 0;
    }
    // Add null checks for props.orderSummary and props.orderSummary.shipping
    const priceCurrency =
        props.orderSummary && props.orderSummary.shipping && props.orderSummary.shipping.props
            ? (props.orderInfomation &&
                  props.orderInfomation.amount &&
                  props.orderInfomation.amount.props &&
                  props.orderInfomation.amount.props.currencyCode &&
                  props.orderInfomation.amount.props.currencyCode) ||
              (props.orderInfomation &&
                  props.orderInfomation.amount &&
                  props.orderInfomation.amount.props &&
                  props.orderInfomation.amount.props.currencyCode) ||
              'USD'
            : (props.orderInfomation &&
                  props.orderInfomation.amount &&
                  props.orderInfomation.amount.props &&
                  props.orderInfomation.amount.props.currencyCode) ||
              'USD'; // Default to USD if not available

    // Ensure we have valid numbers before formatting
    const subtotalCost =
        props.context && props.context.cultureFormatter
            ? props.context.cultureFormatter.formatCurrency(subtotalAmount || 0, priceCurrency)
            : '$0.00';

    const shippingCost =
        props.context && props.context.cultureFormatter
            ? props.context.cultureFormatter.formatCurrency(shippingAmount || 0, priceCurrency)
            : '$0.00';

    const taxCost =
        props.context && props.context.cultureFormatter
            ? props.context.cultureFormatter.formatCurrency(taxAmount || 0, priceCurrency)
            : '$0.00';

    const totalCost =
        props.context && props.context.cultureFormatter
            ? props.context.cultureFormatter.formatCurrency(totalAmount || 0, priceCurrency)
            : '$0.00';

    return (
        <div className='lds-order-summary-details'>
            <div className='lds-order-summary-details-container'>
                <div className='lds-order-summary-details-content'>
                    <div className='lds-order-summary-details-row'>
                        <div className='lds-order-summary-details-label'>{props.resources.subTotalLabel}</div>
                        <div className='lds-order-summary-details-value'>{subtotalCost}</div>
                    </div>
                    <div className='lds-order-summary-details-row'>
                        <div className='lds-order-summary-details-label'>{props.resources.shippingText}</div>
                        <div className='lds-order-summary-details-value'>{shippingCost}</div>
                    </div>
                    <div className='lds-order-summary-details-row'>
                        <div className='lds-order-summary-details-label'>{props.resources.taxLabel}</div>
                        <div className='lds-order-summary-details-value'>{taxCost}</div>
                    </div>
                </div>
                <div className='lds-order-summary-details-total'>
                    <div className='lds-order-summary-details-row'>
                        <div className='lds-order-summary-details-label'>{props.resources.orderTotal}</div>
                        <div className='lds-order-summary-details-value'>{totalCost}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const OrderDetailsView: React.FC<IOrderDetailsViewProps> = props => {
    console.log(props);
    const { moduleProps, orderInfomation, groups, context, resources, salesOrder } = props;
    const [bopisStore, setBopisStore] = React.useState<any>();
    const [channel, setChannel] = React.useState<any>();
    const [warehouse, setWarehouse] = React.useState<any>();
    console.log(bopisStore);

    let nonSubItems: any = [];
    let subItems: any = [];
    if (groups && groups.groups && groups.groups[0]) {
        nonSubItems = groups.groups[0].salesLines.filter(salesLine => {
            //@ts-ignore
            return salesLine.data.product?.ExtensionProperties[0].Value?.BooleanValue === false;
        });
        subItems = groups.groups[0].salesLines.filter(salesLine => {
            //@ts-ignore
            return salesLine.data.product?.ExtensionProperties[0].Value?.BooleanValue === true;
        });
    }
    console.log(nonSubItems, subItems);
    let bankDepositPrimary;
    let bankDepositSecondary;
    if (salesOrder && salesOrder.AttributeValues) {
        bankDepositPrimary = salesOrder.AttributeValues.filter((attribute: any) => {
            return attribute.Name === 'BankDepositPrimaryMessage';
        });
        bankDepositSecondary = salesOrder.AttributeValues.filter((attribute: any) => {
            return attribute.Name === 'BankDepositSecondaryMessage';
        });
    }
    //@ts-ignore
    let itemCount = orderInfomation && orderInfomation.count ? orderInfomation.count.props.children : '';
    let wardId = context.request.query && context.request.query.wardId ? context.request.query.wardId : '';

    let pickupStore: any;

    const getPickupStore = async () => {
        const ctx = { callerContext: context.actionContext };
        let stores = await getStoreHoursAsync(ctx, 'ZAF');
        if (stores) {
            pickupStore = stores.filter((store: any) => {
                return store.ZIPCODE === salesOrder?.ShippingAddress?.ZipCode && store.STREET === salesOrder?.ShippingAddress?.Street;
            });
        }
        setBopisStore(pickupStore[0]);

        return stores;
    };

    const getRetailChannel = async () => {
        const ctx = { callerContext: context.actionContext };

        // @ts-ignore
        const channel = await getRetailChannelAsync(ctx, context.request.channel?.RecordId);
        let warehouseId = salesOrder && salesOrder.InventoryLocationId ? salesOrder.InventoryLocationId : '';

        const warehouse = await getWarehouseAsync(ctx, warehouseId);

        setChannel(channel);
        setWarehouse(warehouse);
    };

    React.useEffect(() => {
        void getPickupStore();
        void getRetailChannel();
    }, []);
    if (!props.orderSummary && !props.orderInfomation) {
        // @ts-ignore
        return <h2>{props.resources.noDetailsFound}</h2>;
    }

    return (
        <Module {...moduleProps}>
            {channel && warehouse && orderInfomation && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        justifyContent: 'space-around',
                        marginBottom: channel && warehouse ? '20px' : ''
                    }}
                >
                    {channel && channel.NotificationCity ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h3>{channel.NotificationDescription || ''}</h3>
                            <div>{channel.NotificationStreet || ''}</div>
                            <div>
                                {channel.NotificationCity || ''}, {channel.NotificationState || ''} {channel.NotificationZipCode || ''}
                            </div>
                            <div>{channel.NotificationRegionId || ''}</div>
                        </div>
                    ) : null}
                    {warehouse && warehouse.NotificationCity ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h3>{warehouse.NotificationDescription || ''}</h3>
                            <div>{warehouse.NotificationStreet || ''}</div>
                            <div>
                                {warehouse.NotificationCity || ''}, {warehouse.NotificationState || ''}{' '}
                                {warehouse.NotificationZipCode || ''}
                            </div>
                            <div>{warehouse.NotificationRegionId || ''}</div>
                        </div>
                    ) : null}
                </div>
            )}
            {/* @ts-ignore */}
            {bankDepositSecondary && bankDepositSecondary[0] ? bankDepositSecondary[0].TextValue : ''}
            {bankDepositPrimary && bankDepositPrimary[0] ? (
                // @ts-ignore
                <div dangerouslySetInnerHTML={{ __html: bankDepositPrimary[0].TextValue }} />
            ) : (
                ''
            )}
            <OrderDetailsHeading {...props} />
            <OrderDetailsBody {...props} />
            {props && props.orderSummary && props.orderSummary.shipping === '' ? (
                <OrderPickupDetails {...props} />
            ) : (
                <OrderShippingDetails {...props} />
            )}
            <OrderItemDetails {...props} />
            <OrderSummaryDetails {...props} />
            {/* @ts-ignore */}
            {wardId && <h3>{`${resources.billToLabel} ${wardId}`}</h3>}
        </Module>
    );
};

export default OrderDetailsView;
